import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { openNotification } from '../UI';
import {
  UpdateFirmMemberEmailNotificationSettingsInput,
  UpdateFirmMemberInput,
} from '../generated/graphql';
import {
  ErrorCode,
  clientUpdateFirmMember,
  clientUpdateFirmMemberEmailNotificationSettings,
  hasErrorCode,
  useGraphqlClient,
} from '../graphql';
import { firmMemberQueryKeys } from './queryKeys';

export const useUpdateFirmMember = () => {
  const { t: tFirmMember } = useTranslation('firmMember');
  const gqlClient = useGraphqlClient();

  const updateFirmMember = useMutation({
    mutationFn: (input: UpdateFirmMemberInput) => {
      return clientUpdateFirmMember(gqlClient, input);
    },
    onError: () => {
      openNotification({
        type: 'error',
        description: tFirmMember('error-unable-to-update-email-signature'),
      });
    },
  });

  return { updateFirmMember };
};

export const useUpdateFirmMemberEmailNotificationSettings = () => {
  const { t: tFirmMember } = useTranslation('firmMember');
  const gqlClient = useGraphqlClient();
  const queryClient = useQueryClient();

  const updateFirmMemberEmailNotificationSettings = useMutation({
    mutationFn: (input: UpdateFirmMemberEmailNotificationSettingsInput) =>
      clientUpdateFirmMemberEmailNotificationSettings(gqlClient, input),
    onSuccess: (payload) => {
      // Seed the cache with the persisted settings so the fresh changeToken is
      // threaded into the next write without an extra round-trip.
      queryClient.setQueryData(
        firmMemberQueryKeys.emailNotificationSettings(
          payload.emailNotificationSettings.firmMemberID
        ),
        payload.emailNotificationSettings
      );
    },
    onError: (error, input) => {
      // A concurrent write (e.g. another tab) invalidates our changeToken. Refetch
      // so the UI resyncs to the stored state and the member can retry.
      if (hasErrorCode(error, ErrorCode.OldChangeToken)) {
        queryClient.invalidateQueries(
          firmMemberQueryKeys.emailNotificationSettings(input.firmMemberID)
        );
        openNotification({
          type: 'error',
          description: tFirmMember(
            'error-email-notification-settings-conflict'
          ),
        });
        return;
      }
      openNotification({
        type: 'error',
        description: tFirmMember(
          'error-unable-to-update-email-notification-settings'
        ),
      });
    },
  });

  return { updateFirmMemberEmailNotificationSettings };
};
